import { io } from 'socket.io-client';
import axios from 'axios';
import { db } from '../db';
import { addSyncBreadcrumb, captureError, trackAuthFailure } from './errorTracking';
import { toast } from 'sonner';
import { ReticleSignal, signal } from '@/reticle';

// Entity name mapping for user-friendly messages
const ENTITY_NAMES = {
    'patient': 'Patient',
    'appointment': 'Appointment',
    'facility_charge': 'Facility Charge',
    'referral-doctor': 'Referral Doctor',
    'doctor': 'Profile',
    'settings': 'Settings',
    'note': 'Note',
    'fee_agreement': 'Fee Agreement'
};

import { IS_TEST } from '@/config';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export function buildAppointmentRollbackData(appointment, fields, facilityCharge = null) {
    const appointmentSnapshot = {};
    for (const field of fields) appointmentSnapshot[field] = appointment?.[field];
    appointmentSnapshot.updated_at = appointment?.updated_at;
    appointmentSnapshot._synced = appointment?._synced;
    appointmentSnapshot.sync_status = appointment?.sync_status;
    appointmentSnapshot.sync_error = appointment?.sync_error;
    return {
        appointment: appointmentSnapshot,
        facility_charge: facilityCharge ? { ...facilityCharge } : null,
    };
}

class SyncService {
    constructor() {
        this.socket = null;
        this.isSyncing = false;
        this.token = null;
        this.user_id = null;
        this.onUnauthorized = null;
        this.isInitialSyncComplete = false;
        this.syncStateListeners = new Set();
    }

    // Observable pattern for sync state
    onSyncStateChange(callback) {
        this.syncStateListeners.add(callback);
        return () => this.syncStateListeners.delete(callback);
    }

    notifySyncStateChange() {
        this.syncStateListeners.forEach(cb => cb(this.isInitialSyncComplete));
    }

    setOnUnauthorized(callback) {
        this.onUnauthorized = callback;
    }

    initialize(token, user_id, options = {}) {
        // If already initialized, update token for future reconnections
        if (this.socket) {
            this.token = token;
            this.user_id = user_id;
            // Update query for reconnection - not needed if we rely on join event
            // this.socket.io.opts.query = { token };
            // If socket is disconnected, this might help it reconnect with new token
            if (this.socket.disconnected) {
                this.socket.connect();
            }
            return;
        }

        this.token = token;
        this.user_id = user_id;
        const { connectSocket = true } = options;

        // Connect Socket
        // Skip socket connection in tests to prevent timeouts/errors
        // Allow overriding via options, default to skipping if IS_TEST is true
        const shouldConnect = connectSocket && !IS_TEST;

        if (shouldConnect) {
            this.socket = io(API_URL);

            this.socket.on('connect', () => {
                console.log('Socket connected');
                this.socket.emit('join', { token });
                this.processQueue(); // Try syncing when online
                this.pullData(); // Pull latest data on reconnect
            });

            this.socket.on('joined', (data) => {
                console.log('Joined room:', data.room);
            });

            // Listen for updates
            this.socket.on('patient_updated', (data) => this.handleRemoteUpdate('patients', data));
            this.socket.on('patient_deleted', (data) => this.handleRemoteDelete('patients', data.id));
            this.socket.on('appointment_updated', (data) => this.handleRemoteUpdate('appointments', data));
            this.socket.on('user_updated', (data) => this.handleRemoteUpdate('users', data));
            this.socket.on('settings_updated', (data) => this.handleRemoteUpdate('settings', data));
            this.socket.on('facility_charge_updated', (data) => this.handleRemoteUpdate('facility_charges', data));
            this.socket.on('facility_charge_deleted', (data) => this.handleRemoteDelete('facility_charges', data.id));
            this.socket.on('facility_charges_paid', (data) => this.handleFacilityChargesPaid(data));
            this.socket.on('note_updated', (data) => this.handleRemoteUpdate('appointment_notes', data));
            this.socket.on('note_deleted', (data) => this.handleRemoteDelete('appointment_notes', data.id));
            // Clinic events
            this.socket.on('clinic_updated', (data) => this.handleRemoteUpdate('clinics', data));
            this.socket.on('clinic_member_updated', (data) => this.handleRemoteUpdate('clinic_members', data));
            this.socket.on('clinic_member_removed', (data) => this.handleRemoteDelete('clinic_members', data.id));
            this.socket.on('fee_agreement_updated', (data) => this.handleRemoteUpdate('fee_agreements', data));
            this.socket.on('fee_agreement_deleted', (data) => this.handleRemoteDelete('fee_agreements', data.id));
        } else {
            console.log('Test mode: Skipping socket connection');
        }

        // Start periodic sync (every 30s)
        setInterval(() => this.processQueue(), 30000);

        // Initial Pull
        this.pullData();
    }

    async handleRemoteUpdate(table, data) {
        try {
            console.log(`Remote update for ${table}:`, data);

            // If patient is marked as deleted in the update, handle it
            if (table === 'patients' && data.deleted) {
                await this.handleRemoteDelete('patients', data.id);
                return;
            }

            // Mark data from server as synced
            const syncedData = { ...data, _synced: true };

            // Use smartPut with special facility charge handling
            if (table === 'facility_charges') {
                const localItem = await db[table].get(data.id);
                if (localItem && localItem.paid_status === 'paid' && data.paid_status === 'pending') {
                    // Don't overwrite paid status with pending
                    // Don't overwrite paid status with pending

                    // Always keep local paid status if remote is pending, regardless of timestamps.
                    await db[table].put({
                        ...data,
                        paid_status: localItem.paid_status,
                        payment_date: localItem.payment_date,
                        updated_at: localItem.updated_at,
                        _synced: true
                    });
                    return;
                }
            }

            await this.smartPut(table, syncedData);
        } catch (error) {
            console.error(`Error handling remote update for ${table}:`, error);
        }
    }

    async handleRemoteDelete(table, id) {
        try {
            console.log(`Remote delete for ${table}:`, id);
            if (table === 'patients') {
                // Soft delete patient in Dexie
                await db.patients.update(id, { deleted: true, _synced: true });
                // Delete associated scheduled appointments
                await db.appointments.where('patient_id').equals(id).filter(a => a.status === 'scheduled').delete();
            } else {
                await db[table].delete(id);
            }
        } catch (error) {
            console.error(`Error handling remote delete for ${table}:`, error);
        }
    }

    async handleFacilityChargesPaid(data) {
        try {
            // data.ids is list of facility charge IDs
            await db.facility_charges.where('id').anyOf(data.ids).modify({
                paid_status: 'paid',
                payment_date: data.payment_date,
                updated_at: data.updated_at
            });
        } catch (error) {
            console.error('Error handling facility_charges paid:', error);
        }
    }

    async pullData() {
        addSyncBreadcrumb('pull_started');
        try {
            const lastSync = localStorage.getItem('last_sync');
            const response = await axios.get(`${API_URL}/api/sync`, {
                params: { last_sync: lastSync },
                headers: { Authorization: `Bearer ${this.token}` }
            });

            const { patients, appointments, facility_charges, settings, doctor, referral_doctors, appointment_notes, clinics, clinic_members, patient_doctor_links, fee_agreements, plans, user_subscriptions, user_addons, addons, invoices, form_templates, form_template_versions, form_assessments, form_assessment_revisions, timestamp } = response.data;

            await db.transaction('rw', db.patients, db.appointments, db.facility_charges, db.settings, db.users, db.appointment_notes, db.clinics, db.clinic_members, db.patient_doctor_links, db.fee_agreements, db.plans, db.user_subscriptions, db.user_addons, db.addons, db.invoices, db.form_templates, db.form_template_versions, db.form_assessments, db.form_assessment_revisions, db.form_assessment_drafts, db.sync_queue, async () => {
                if (patients && patients.length) await this.smartMerge('patients', patients);
                if (appointments && appointments.length) await this.smartMerge('appointments', appointments);
                if (facility_charges && facility_charges.length) await this.smartMerge('facility_charges', facility_charges);
                if (appointment_notes && appointment_notes.length) await this.smartMerge('appointment_notes', appointment_notes);
                if (clinics && clinics.length) await this.smartMerge('clinics', clinics);
                if (clinic_members && clinic_members.length) await this.smartMerge('clinic_members', clinic_members);
                if (patient_doctor_links && patient_doctor_links.length) await this.smartMerge('patient_doctor_links', patient_doctor_links);
                if (fee_agreements && fee_agreements.length) await this.smartMerge('fee_agreements', fee_agreements);

                // Subscription Data
                if (plans && plans.length) await this.smartMerge('plans', plans);
                if (user_subscriptions && user_subscriptions.length) await this.smartMerge('user_subscriptions', user_subscriptions);
                if (user_addons && user_addons.length) await this.smartMerge('user_addons', user_addons);
                if (addons && addons.length) await this.smartMerge('addons', addons);

                // Invoices
                if (invoices && invoices.length) await this.smartMerge('invoices', invoices);

                if (settings) await this.smartPut('settings', settings, 'user_id');
                if (doctor) await this.smartPut('users', doctor);
                if (referral_doctors && referral_doctors.length) await this.smartMerge('users', referral_doctors);

                // Custom Forms - Authoritative revocation cleanup check
                if (doctor && doctor.custom_forms_access === false) {
                    await this.clearRevokedFormCache({ withinTransaction: true });
                } else {
                    if (form_templates && form_templates.length) await this.smartMerge('form_templates', form_templates);
                    if (form_template_versions && form_template_versions.length) await this.smartMerge('form_template_versions', form_template_versions);
                    if (form_assessments && form_assessments.length) await this.smartMerge('form_assessments', form_assessments);
                    if (form_assessment_revisions && form_assessment_revisions.length) await this.smartMerge('form_assessment_revisions', form_assessment_revisions);
                }
            });


            localStorage.setItem('last_sync', timestamp);
            console.log('Pull complete');
            addSyncBreadcrumb('pull_complete', { itemCount: (patients?.length || 0) + (appointments?.length || 0) });
            this.isInitialSyncComplete = true;
            this.notifySyncStateChange();
            return true;
        } catch (error) {
            console.error('Pull error:', error);
            captureError(error, { operation: 'pullData', lastSync: localStorage.getItem('last_sync') });


            // Note: Don't call onUnauthorized for 401 here - the axios interceptor in App.js
            // handles token refresh automatically. Calling logout here caused race conditions.
            if (error.response && error.response.status === 401) {
                trackAuthFailure('pull_unauthorized');
                // Axios interceptor will handle refresh and retry
            }
            // Even on error, mark sync as complete to not block UI
            addSyncBreadcrumb('pull_failed', { error: error.message });
            this.isInitialSyncComplete = true;
            this.notifySyncStateChange();
            return false;
        }
    }

    async smartMerge(table, items, keyField = 'id') {
        if (!items || items.length === 0) return;

        const keys = items.map(i => i[keyField]);
        const localItems = await db[table].bulkGet(keys);

        const itemsToPut = [];

        for (let i = 0; i < items.length; i++) {
            const remoteItem = { ...items[i], _synced: true }; // Mark items from server as synced
            const localItem = localItems[i];

            if (!localItem) {
                itemsToPut.push({ ...remoteItem, _synced: true });
            } else {
                // Special handling for facility_charges to prevent reverting 'paid' to 'pending'
                if (table === 'facility_charges' && localItem.paid_status === 'paid' && remoteItem.paid_status === 'pending') {
                    // Always keep local paid status if remote is pending, regardless of timestamps.
                    // This prevents backend creation timestamp (which might be later) from overwriting local payment.
                    itemsToPut.push({
                        ...remoteItem,
                        paid_status: localItem.paid_status,
                        payment_date: localItem.payment_date,
                        updated_at: localItem.updated_at, // Keep local timestamp
                        _synced: true
                    });
                } else {
                    const remoteDate = remoteItem.updated_at ? new Date(remoteItem.updated_at) : new Date(0);
                    const localDate = localItem.updated_at ? new Date(localItem.updated_at) : new Date(0);

                    // If remote is newer or equal, accept it.
                    if (remoteDate >= localDate) {
                        itemsToPut.push({ ...remoteItem, _synced: true });
                    }
                }
            }
        }

        if (itemsToPut.length > 0) {
            await db[table].bulkPut(itemsToPut);
        }
    }



    async smartPut(table, item, keyField = 'id') {
        if (!item) return;
        const syncedItem = { ...item, _synced: true }; // Mark items from server as synced
        const localItem = await db[table].get(item[keyField]);
        if (!localItem) {
            await db[table].put(syncedItem);
        } else {
            const remoteDate = item.updated_at ? new Date(item.updated_at) : new Date(0);
            const localDate = localItem.updated_at ? new Date(localItem.updated_at) : new Date(0);
            if (remoteDate >= localDate) {
                // Merge localItem and syncedItem to preserve fields missing in partial updates
                await db[table].put({ ...localItem, ...syncedItem });
            }
        }
    }

    async processQueue() {
        if (this.isSyncing) return;
        this.isSyncing = true;
        let passSucceeded = true;
        let continueProcessing = false;

        try {
            const queue = await db.sync_queue.orderBy('created_at').toArray();
            if (queue.length === 0) return;

            console.log(`Processing ${queue.length} items in sync queue`);

            for (const queuedItem of queue) {
                const item = await db.sync_queue.get(queuedItem.id);
                if (!item || item.status === 'cancelled') continue;
                try {
                    await this.syncItem(item);
                    await db.sync_queue.delete(item.id);

                    // Mark the entity as synced in the local database
                    if (item.entity === 'facility_charge_payment' && item.data?.charge_ids) {
                        // Special handling for facility charge payments - mark all facility charge IDs as synced
                        await this.markFacilityChargesAsSynced(item.data.charge_ids);
                    } else if (item.entity_id) {
                        await this.markAsSynced(item.entity, item.entity_id);
                    }
                } catch (error) {
                    passSucceeded = false;
                    console.error('Sync item error:', error);
                    // If network error, stop processing queue
                    if (!error.response) break;

                    // If unauthorized, stop processing queue and let axios interceptor handle refresh
                    // Don't call onUnauthorized - that causes race conditions with the interceptor
                    if (error.response.status === 401) {
                        trackAuthFailure('sync_queue_unauthorized', { entity: item.entity, type: item.type });
                        // Axios interceptor will handle refresh - break to avoid hammering API
                        break;
                    }

                    // If 404 error, the resource doesn't exist on server - remove from queue
                    if (error.response.status === 404) {
                        let errorMsg = error.response.data?.detail || error.response.data?.message || "Resource not found";
                        if (typeof errorMsg === 'object') {
                            errorMsg = errorMsg.message || JSON.stringify(errorMsg);
                        }
                        if (await this.handleCustomFormSyncError(item, errorMsg, error.response.status)) {
                            await db.sync_queue.delete(item.id);
                            continue;
                        }
                        console.warn(`Removing failed item from queue (404): ${item.entity} ${item.type}`, item.data?.id);
                        await db.sync_queue.delete(item.id);
                        continue;
                    }
                    // If 403 error, user doesn't have permission - remove from queue and mark as failed
                    if (error.response.status === 403) {
                        let errorMsg = error.response.data?.detail || error.response.data?.message || "Permission denied";
                        if (typeof errorMsg === 'object') {
                            errorMsg = errorMsg.message || JSON.stringify(errorMsg);
                        }
                        console.warn(`Removing failed item from queue (403): ${item.entity} ${item.type}`, item.data?.id, errorMsg);

                        // Show toast for sync failure
                        const entityName = ENTITY_NAMES[item.entity] || item.entity;
                        toast.error(`Failed to sync ${entityName}`, {
                            description: errorMsg,
                            duration: 5000
                        });

                        if (await this.handleCustomFormSyncError(item, errorMsg, error.response.status)) {
                            await db.sync_queue.delete(item.id);
                            continue;
                        }

                        const restoredAppointment = await this.restoreRejectedAppointmentUpdate(item);
                        if (!restoredAppointment) {
                            // Update the local entity to show as failed
                            try {
                                const tableMap = {
                                    'patient': 'patients',
                                    'appointment': 'appointments',
                                    'facility_charge': 'facility_charges',
                                    'referral-doctor': 'users',
                                    'doctor': 'users',
                                    'settings': 'settings',
                                    'note': 'appointment_notes',
                                    'fee_agreement': 'fee_agreements',
                                    'clinic': 'clinics'
                                };
                                const table = tableMap[item.entity];
                                if (table && item.entity_id) {
                                    await db[table].update(item.entity_id, {
                                        _synced: false,
                                        sync_status: 'failed',
                                        sync_error: errorMsg
                                    });
                                }
                            } catch (updateErr) {
                                console.error("Failed to update entity status on sync failure:", updateErr);
                            }
                        }

                        await db.sync_queue.delete(item.id);
                        continue;
                    }
                    // If 400 error, it's a bad request - remove from queue to prevent infinite retries
                    if (error.response.status === 400) {
                        let errorMsg = error.response.data?.detail || error.response.data?.message || "Invalid request";
                        if (typeof errorMsg === 'object') {
                            errorMsg = errorMsg.message || JSON.stringify(errorMsg);
                        }

                        // Special Handling for "Already Linked" / "Already Exists" scenarios
                        if (item.entity === 'patient' && item.type === 'create' &&
                            (errorMsg.includes("already in your list") || errorMsg.includes("already exists"))) {
                            console.log("Conflict detected: Patient already exists. Attempting reconciliation.");
                            try {
                                // 1. Fetch Global Data by Phone
                                const config = { headers: { Authorization: `Bearer ${this.token}` } };
                                const searchRes = await axios.get(`${API_URL}/api/patients/search?q=${item.data.phone}`, config);
                                // The search API returns fuzzy matches, we need exact phone match
                                const globalPatient = searchRes.data.find(p => p.phone === item.data.phone);

                                if (globalPatient) {
                                    console.log(`Reconciling local patient ${item.data.id} with global ${globalPatient.id}`);
                                    // 2. Remap Local ID to Global ID
                                    if (globalPatient.id !== item.data.id) {
                                        await db.transaction('rw', db.patients, db.appointments, db.facility_charges, db.appointment_notes, async () => {
                                            // Update appointments
                                            await db.appointments.where('patient_id').equals(item.data.id).modify({ patient_id: globalPatient.id });

                                            // Delete local temp
                                            await db.patients.delete(item.data.id);

                                            // Add global (marked synced)
                                            // Ensure we merge any local notes if important? No, global is truth.
                                            await db.patients.put({ ...globalPatient, _synced: true });
                                        });
                                    } else {
                                        // ID match (unlikely for new local creation, but possible)
                                        await db.patients.put({ ...globalPatient, _synced: true });
                                        await db.patients.update(globalPatient.id, { _synced: true });
                                    }

                                    // Remove from queue and continue
                                    await db.sync_queue.delete(item.id);
                                    continue;
                                }
                            } catch (recError) {
                                console.error("Reconciliation failed:", recError);
                                // Fall through to standard failure marking
                            }
                        }

                        console.warn(`Removing failed item from queue (400): ${item.entity} ${item.type}`, item.data?.id, errorMsg);

                        // Show toast for sync failure
                        const entityName = ENTITY_NAMES[item.entity] || item.entity;
                        toast.error(`Failed to sync ${entityName}`, {
                            description: errorMsg,
                            duration: 5000
                        });

                        if (await this.handleCustomFormSyncError(item, errorMsg, error.response.status)) {
                            await db.sync_queue.delete(item.id);
                            continue;
                        }

                        // Update the local entity to show as failed
                        try {
                            const tableMap = {
                                'patient': 'patients',
                                'appointment': 'appointments',
                                'facility_charge': 'facility_charges',
                                'referral-doctor': 'users',
                                'doctor': 'users',
                                'settings': 'settings',
                                'note': 'appointment_notes',
                                'fee_agreement': 'fee_agreements',
                                'clinic': 'clinics'
                            };
                            const table = tableMap[item.entity];
                            if (table && item.entity_id) {
                                await db[table].update(item.entity_id, {
                                    _synced: false, // Keep as unsynced
                                    sync_status: 'failed',
                                    sync_error: errorMsg
                                });
                            }
                        } catch (updateErr) {
                            console.error("Failed to update entity status on sync failure:", updateErr);
                        }

                        await db.sync_queue.delete(item.id);
                        continue;
                    }
                    // If 422 error (Unprocessable Entity), remove from queue to prevent infinite retries
                    if (error.response.status === 422) {
                        let errorMsg = error.response.data?.detail || error.response.data?.message || "Validation failed";
                        if (typeof errorMsg === 'object') {
                            errorMsg = errorMsg.message || JSON.stringify(errorMsg);
                        }
                        console.warn(`Removing failed item from queue (422): ${item.entity} ${item.type}`, item.data?.id, errorMsg);

                        // Show toast for sync failure
                        const entityName = ENTITY_NAMES[item.entity] || item.entity;
                        toast.error(`Failed to sync ${entityName}`, {
                            description: errorMsg,
                            duration: 5000
                        });

                        if (await this.handleCustomFormSyncError(item, errorMsg, error.response.status)) {
                            await db.sync_queue.delete(item.id);
                            continue;
                        }

                        // Update the local entity to show as failed
                        try {
                            const tableMap = {
                                'patient': 'patients',
                                'appointment': 'appointments',
                                'facility_charge': 'facility_charges',
                                'referral-doctor': 'users',
                                'doctor': 'users',
                                'settings': 'settings',
                                'note': 'appointment_notes',
                                'fee_agreement': 'fee_agreements',
                                'clinic': 'clinics'
                            };
                            const table = tableMap[item.entity];
                            if (table && item.entity_id) {
                                await db[table].update(item.entity_id, {
                                    _synced: false,
                                    sync_status: 'failed',
                                    sync_error: errorMsg
                                });
                            }
                        } catch (updateErr) {
                            console.error("Failed to update entity status on sync failure:", updateErr);
                        }

                        await db.sync_queue.delete(item.id);
                        continue;
                    }
                    // If 409 error (Conflict), remove from queue to prevent infinite retries
                    if (error.response.status === 409) {
                        let errorMsg = error.response.data?.detail || error.response.data?.message || "Conflict occurred";
                        if (typeof errorMsg === 'object') {
                            errorMsg = errorMsg.message || JSON.stringify(errorMsg);
                        }
                        console.warn(`Removing failed item from queue (409): ${item.entity} ${item.type}`, item.data?.id, errorMsg);

                        // Show toast for sync failure
                        const entityName = ENTITY_NAMES[item.entity] || item.entity;
                        toast.error(`Failed to sync ${entityName}`, {
                            description: errorMsg,
                            duration: 5000
                        });

                        if (await this.handleCustomFormSyncError(item, errorMsg, error.response.status)) {
                            await db.sync_queue.delete(item.id);
                            continue;
                        }

                        // Update the local entity to show as failed
                        try {
                            const tableMap = {
                                'patient': 'patients',
                                'appointment': 'appointments',
                                'facility_charge': 'facility_charges',
                                'referral-doctor': 'users',
                                'doctor': 'users',
                                'settings': 'settings',
                                'note': 'appointment_notes',
                                'fee_agreement': 'fee_agreements',
                                'clinic': 'clinics'
                            };
                            const table = tableMap[item.entity];
                            if (table && item.entity_id) {
                                await db[table].update(item.entity_id, {
                                    _synced: false,
                                    sync_status: 'failed',
                                    sync_error: errorMsg
                                });
                            }
                        } catch (updateErr) {
                            console.error("Failed to update entity status on sync failure:", updateErr);
                        }

                        await db.sync_queue.delete(item.id);
                        continue;
                    }
                    // For 500 errors, keep trying
                }
            }
            if (passSucceeded) {
                const pending = await db.sync_queue.where('status').equals('pending').count();
                if (pending === 0) {
                    signal(ReticleSignal.SYNC_IDLE, { pending: 0 });
                } else {
                    // New records can be queued while this pass is awaiting the server.
                    // Drain those records immediately, but never continue after a failed pass.
                    continueProcessing = true;
                }
            }
        } finally {
            addSyncBreadcrumb('queue_processed');
            this.isSyncing = false;
        }
        if (continueProcessing) return this.processQueue();
    }

    async markAsSynced(entity, entityId) {
        try {
            const tableMap = {
                'patient': 'patients',
                'appointment': 'appointments',
                'facility_charge': 'facility_charges',
                'referral-doctor': 'users',
                'doctor': 'users',
                'settings': 'settings',
                'note': 'appointment_notes',
                'fee_agreement': 'fee_agreements',
                'clinic': 'clinics'
            };
            const table = tableMap[entity];
            if (table && entityId) {
                await db[table].update(entityId, { _synced: true });
                console.log(`Marked ${entity} ${entityId} as synced`);
            }
        } catch (error) {
            console.error(`Error marking ${entity} ${entityId} as synced:`, error);
        }
    }

    async markFacilityChargesAsSynced(chargeIds) {
        try {
            await db.facility_charges.where('id').anyOf(chargeIds).modify({ _synced: true });
            console.log(`Marked ${chargeIds.length} facility_charges as synced`);
        } catch (error) {
            console.error('Error marking facility_charges as synced:', error);
        }
    }

    async handleCustomFormSyncError(item, errorMsg, status) {
        if (['form_assessment_submit', 'form_assessment_correction', 'form_assessment_entered_in_error'].includes(item.entity)) {
            const draftId = item.data?.draft_id;
            const assessmentId = item.entity_id;
            await db.transaction('rw', db.form_assessment_drafts, db.form_assessments, async () => {
                if (draftId && (item.entity === 'form_assessment_submit' || item.entity === 'form_assessment_correction')) {
                    await db.form_assessment_drafts.update(draftId, {
                        state: status === 409 ? 'conflict' : 'failed',
                        sync_error: errorMsg,
                        sync_error_status: status
                    });
                }
                if (assessmentId) {
                    await db.form_assessments.update(assessmentId, {
                        _synced: false,
                        sync_status: 'failed',
                        sync_error: errorMsg
                    });
                }
            });
            return true;
        }
        return false;
    }

    async applyAssessmentAcknowledgement(response, draftId) {
        const { assessment, revision } = response.data;
        await db.transaction('rw', db.form_assessments, db.form_assessment_revisions, db.form_assessment_drafts, async () => {
            await db.form_assessments.put({ ...assessment, _synced: true, sync_status: 'synced', sync_error: null });
            await db.form_assessment_revisions.put({ ...revision, _synced: true });
            if (draftId) await db.form_assessment_drafts.delete(draftId);
        });
    }

    async resolveConflictAndRetry(draftId, entity) {
        const draft = await db.form_assessment_drafts.get(draftId);
        if (!draft) return;

        const now = new Date().toISOString();

        if (entity === 'form_assessment_submit') {
            const oldAssessmentId = draft.assessment_id;
            const oldRevisionId = draft.revision_id;
            const newId = crypto.randomUUID();
            const newRevisionId = crypto.randomUUID();

            const updatedDraft = {
                ...draft,
                assessment_id: newId,
                revision_id: newRevisionId,
                state: 'pending',
                sync_error: null,
                sync_error_status: null,
                updated_at: now
            };

            await db.transaction('rw', db.form_assessment_drafts, db.form_assessments, db.form_assessment_revisions, db.sync_queue, async () => {
                await db.form_assessment_drafts.put(updatedDraft);

                // Delete the old conflicted record so it doesn't linger as a ghost
                // with sync_status: 'failed' and appear in UI views.
                await db.form_assessments.delete(oldAssessmentId);
                await db.form_assessment_revisions.delete(oldRevisionId);

                await db.form_assessments.put({
                    id: updatedDraft.assessment_id, appointment_id: updatedDraft.appointment_id, template_id: updatedDraft.template_id,
                    template_version_id: updatedDraft.template_version_id, author_id: updatedDraft.author_id, clinic_id: updatedDraft.clinic_id,
                    latest_revision_number: updatedDraft.base_revision_number + 1,
                    status: 'submitted', sync_status: 'pending', sync_error: null, _synced: false, created_at: updatedDraft.created_at, updated_at: now,
                });
                await db.form_assessment_revisions.put({
                    id: updatedDraft.revision_id, assessment_id: updatedDraft.assessment_id, revision_number: updatedDraft.base_revision_number + 1,
                    answers: updatedDraft.answers, submitted_by: updatedDraft.author_id,
                    correction_reason: null,
                    submitted_at: now, created_at: now, _synced: false,
                });

                await db.sync_queue.add({
                    type: 'create', entity: 'form_assessment_submit', entity_id: updatedDraft.assessment_id,
                    data: {
                        id: updatedDraft.assessment_id, revision_id: updatedDraft.revision_id, appointment_id: updatedDraft.appointment_id,
                        template_version_id: updatedDraft.template_version_id, answers: updatedDraft.answers, draft_id: updatedDraft.id,
                    }, status: 'pending', created_at: now,
                });
            });
        }

        if (entity === 'form_assessment_correction') {
            const assessmentId = draft.assessment_id;
            const config = { headers: { Authorization: `Bearer ${this.token}` } };
            const response = await axios.get(`${API_URL}/api/form-assessments/${assessmentId}`, config);
            const latestRevisionNumber = response.data.assessment.latest_revision_number;
            const authoritativeRevisions = response.data.revisions || [];

            const newRevisionId = crypto.randomUUID();

            const updatedDraft = {
                ...draft,
                revision_id: newRevisionId,
                base_revision_number: latestRevisionNumber,
                state: 'pending',
                sync_error: null,
                sync_error_status: null,
                updated_at: now
            };

            await db.transaction('rw', db.form_assessment_drafts, db.form_assessments, db.form_assessment_revisions, db.sync_queue, async () => {
                await db.form_assessment_drafts.put(updatedDraft);
                await db.form_assessment_revisions.delete(draft.revision_id);
                if (authoritativeRevisions.length) {
                    await db.form_assessment_revisions.bulkPut(
                        authoritativeRevisions.map(revision => ({ ...revision, _synced: true }))
                    );
                }

                await db.form_assessments.update(updatedDraft.assessment_id, {
                    latest_revision_number: updatedDraft.base_revision_number + 1,
                    sync_status: 'pending', sync_error: null, _synced: false
                });

                await db.form_assessment_revisions.put({
                    id: updatedDraft.revision_id, assessment_id: updatedDraft.assessment_id, revision_number: updatedDraft.base_revision_number + 1,
                    answers: updatedDraft.answers, submitted_by: updatedDraft.author_id,
                    correction_reason: updatedDraft.correction_reason,
                    submitted_at: now, created_at: now, _synced: false,
                });

                await db.sync_queue.add({
                    type: 'create', entity: 'form_assessment_correction', entity_id: updatedDraft.assessment_id,
                    data: {
                        assessment_id: updatedDraft.assessment_id, revision_id: updatedDraft.revision_id,
                        expected_revision_number: updatedDraft.base_revision_number + 1, answers: updatedDraft.answers,
                        correction_reason: updatedDraft.correction_reason, draft_id: updatedDraft.id,
                    }, status: 'pending', created_at: now,
                });
            });
        }
    }

    async syncCustomFormItem(entity, data, config) {
        const { draft_id: draftId, assessment_id: assessmentId, ...payload } = data;
        let response;
        if (entity === 'form_assessment_submit') {
            response = await axios.post(`${API_URL}/api/form-assessments`, payload, config);
        } else if (entity === 'form_assessment_correction') {
            response = await axios.post(`${API_URL}/api/form-assessments/${assessmentId}/revisions`, payload, config);
        } else if (entity === 'form_assessment_entered_in_error') {
            response = await axios.post(`${API_URL}/api/form-assessments/${assessmentId}/entered-in-error`, payload, config);
            await db.form_assessments.put({ ...response.data, _synced: true, sync_status: 'synced', sync_error: null });
            return;
        } else {
            throw new Error(`Unsupported custom-form sync entity: ${entity}`);
        }
        await this.applyAssessmentAcknowledgement(response, draftId);
    }

    async syncItem(item) {
        const config = {
            headers: { Authorization: `Bearer ${this.token}` }
        };

        const { type, entity, data } = item;

        if (['form_assessment_submit', 'form_assessment_correction', 'form_assessment_entered_in_error'].includes(entity)) {
            return await this.syncCustomFormItem(entity, data, config);
        }

        if (entity === 'patient') {

            if (type === 'create') {
                const response = await axios.post(`${API_URL}/api/patients`, data, config);
                const returnedPatient = response.data;

                // Check if backend returned a different ID (existing patient was found globally)
                if (returnedPatient && returnedPatient.id && returnedPatient.id !== data.id) {
                    console.log(`Backend returned different patient ID: ${data.id} -> ${returnedPatient.id}`);

                    try {
                        // Update all local references from old ID to new ID
                        await db.transaction('rw', db.patients, db.appointments, db.facility_charges, db.appointment_notes, db.patient_doctor_links, async () => {
                            // Update appointments that reference the old ID
                            const appointmentsToUpdate = await db.appointments.where('patient_id').equals(data.id).toArray();
                            console.log(`Updating ${appointmentsToUpdate.length} appointments with new patient_id`);
                            for (const apt of appointmentsToUpdate) {
                                await db.appointments.update(apt.id, { patient_id: returnedPatient.id });
                            }

                            // Update appointment notes that reference the old ID (via appointment)
                            // Notes are linked via appointment_id, which we've already updated above

                            // Update patient_doctor_links that reference the old ID
                            const linksToUpdate = await db.patient_doctor_links.where('patient_id').equals(data.id).toArray();
                            console.log(`Updating ${linksToUpdate.length} patient_doctor_links with new patient_id`);
                            for (const link of linksToUpdate) {
                                await db.patient_doctor_links.update(link.id, { patient_id: returnedPatient.id });
                            }

                            // Delete the old patient record and add the returned one
                            await db.patients.delete(data.id);
                            await db.patients.put({ ...returnedPatient, _synced: true });
                        });

                        // Update pending sync queue items that reference the old patient_id
                        // This ensures dependent entities (appointments, fee_agreements) sync with the correct ID
                        const pendingItems = await db.sync_queue.toArray();
                        for (const qItem of pendingItems) {
                            if (qItem.data?.patient_id === data.id) {
                                console.log(`Remapping patient_id in sync queue item: ${qItem.entity} ${qItem.id}`);
                                await db.sync_queue.update(qItem.id, {
                                    data: { ...qItem.data, patient_id: returnedPatient.id }
                                });
                            }
                        }

                        console.log('Patient ID remapping completed successfully');
                    } catch (err) {
                        console.error('Error during patient ID remapping:', err);
                        // Don't throw - still consider sync successful since backend accepted it
                    }
                }
            } else if (type === 'update') {
                await axios.put(`${API_URL}/api/patients/${data.id}`, data, config);
            } else if (type === 'delete') {
                await axios.delete(`${API_URL}/api/patients/${data.id}`, config);
            }
        } else if (entity === 'referral-doctor') {
            const response = await axios.post(`${API_URL}/api/referral-doctors`, data, config);
            const returnedDoctor = response.data;

            // Check if backend returned a different ID (existing doctor was found)
            if (returnedDoctor && returnedDoctor.id && returnedDoctor.id !== data.id) {
                console.log(`Backend returned different doctor ID: ${data.id} -> ${returnedDoctor.id}`);

                try {
                    // Update all local references from old ID to new ID
                    await db.transaction('rw', db.users, db.patients, db.facility_charges, async () => {
                        // Update patients that reference the old ID
                        const patientsToUpdate = await db.patients.where('referral_doctor_id').equals(data.id).toArray();
                        console.log(`Updating ${patientsToUpdate.length} patients with new referral_doctor_id`);
                        for (const patient of patientsToUpdate) {
                            await db.patients.update(patient.id, { referral_doctor_id: returnedDoctor.id });
                        }

                        // Update facility_charges that reference the old ID
                        const chargesToUpdate = await db.facility_charges.where('associated_doctor_id').equals(data.id).toArray();
                        console.log(`Updating ${chargesToUpdate.length} facility_charges with new associated_doctor_id`);
                        for (const charge of chargesToUpdate) {
                            await db.facility_charges.update(charge.id, { associated_doctor_id: returnedDoctor.id });
                        }

                        // Delete the old doctor record and add the returned one
                        await db.users.delete(data.id);
                        await db.users.put({ ...returnedDoctor, _synced: true });
                    });
                    console.log('ID remapping completed successfully');
                } catch (err) {
                    console.error('Error during ID remapping:', err);
                    // Don't throw - still consider sync successful since backend accepted it
                }
            }
        } else if (entity === 'appointment') {
            if (type === 'create') {
                await axios.post(`${API_URL}/api/appointments`, data, config);
            } else if (type === 'update') {
                // Build update payload with all provided fields
                const updatePayload = {};
                if (data.status) updatePayload.status = data.status;
                if (data.cancellation_reason) updatePayload.cancellation_reason = data.cancellation_reason;
                if (data.date_time) updatePayload.date_time = data.date_time;
                if (data.duration_minutes) updatePayload.duration_minutes = data.duration_minutes;
                if (data.fee_amount != null) updatePayload.fee_amount = data.fee_amount;
                if (data.notes != null) updatePayload.notes = data.notes;

                await axios.put(`${API_URL}/api/appointments/${data.id}`, updatePayload, config);
            }
        } else if (entity === 'settings') {
            await axios.put(`${API_URL}/api/settings`, data, config);
        } else if (entity === 'doctor') {
            await axios.put(`${API_URL}/api/profile`, data, config);
        } else if (entity === 'clinic') {
            if (type === 'create') {
                await axios.post(`${API_URL}/api/clinics`, data, config);
            } else if (type === 'update') {
                await axios.put(`${API_URL}/api/clinics/${data.id}`, data, config);
            } else if (type === 'delete') {
                await axios.delete(`${API_URL}/api/clinics/${data.id}`, config);
            }
        } else if (entity === 'clinic_member') {
            if (type === 'create') {
                // Use self-join endpoint — allows any doctor to join a placeholder clinic
                await axios.post(`${API_URL}/api/clinics/${data.clinic_id}/self-join`, {}, config);
            }
        } else if (entity === 'facility_charge') {
            if (type === 'create') {
                await axios.post(`${API_URL}/api/facility-charges`, data, config);
            } else if (type === 'delete') {
                await axios.delete(`${API_URL}/api/facility-charges/${data.id}`, config);
            }
        } else if (entity === 'facility_charge_payment') {
            await axios.post(`${API_URL}/api/facility-charges/pay`, data, config);
        } else if (entity === 'note') {
            // For both create and update, use POST /notes which does upsert
            if (type === 'create' || type === 'update') {
                // Ensure we don't send local binary data if it's there
                // eslint-disable-next-line no-unused-vars
                const { blob, ...metadata } = data;
                await axios.post(`${API_URL}/api/notes`, metadata, config);
            } else if (type === 'delete') {
                await axios.delete(`${API_URL}/api/notes/${data.id}`, config);
            }
        } else if (entity === 'fee_agreement') {
            if (type === 'create') {
                const response = await axios.post(`${API_URL}/api/fee-agreements`, data, config);
                // Update local record with server-generated name (if it was auto-generated)
                const returnedAgreement = response.data;
                if (returnedAgreement?.id && returnedAgreement?.name) {
                    try {
                        await db.fee_agreements.update(returnedAgreement.id, {
                            name: returnedAgreement.name,
                            _synced: true
                        });
                    } catch (e) {
                        console.error('Failed to update fee_agreement name from server:', e);
                    }
                }
            } else if (type === 'update') {
                await axios.put(`${API_URL}/api/fee-agreements/${data.id}`, data, config);
            } else if (type === 'delete') {
                await axios.delete(`${API_URL}/api/fee-agreements/${data.id}`, config);
            }
        }
    }

    async restoreRejectedAppointmentUpdate(item) {
        if (item.entity !== "appointment" || item.type !== "update" || !item.rollback_data?.appointment) return false;
        const current = await db.appointments.get(item.entity_id);
        if (!current || current.updated_at !== item.data?.updated_at) return false;

        const snapshot = item.rollback_data.appointment;
        await db.appointments.update(item.entity_id, {
            ...snapshot,
            _synced: snapshot._synced ?? true,
            sync_status: snapshot.sync_status ?? "synced",
            sync_error: snapshot.sync_error ?? null,
        });

        const charge = item.rollback_data.facility_charge;
        if (charge) {
            await db.facility_charges.put(charge);
            const pending = await db.sync_queue.toArray();
            const dependentDeletes = pending.filter(queueItem =>
                queueItem.id !== item.id && queueItem.entity === "facility_charge" &&
                queueItem.type === "delete" && queueItem.data?.id === charge.id
            );
            await db.sync_queue.bulkDelete(dependentDeletes.map(queueItem => queueItem.id));
        }
        return true;
    }

    async addToQueue(type, entity, data, { rollbackData = null, processImmediately = true } = {}) {
        // Determine entity ID for tracking
        const entityId = data?.id || data?.doctor_id;

        await db.sync_queue.add({
            type,
            entity,
            data,
            entity_id: entityId,
            rollback_data: rollbackData,
            status: 'pending',
            created_at: new Date().toISOString()
        });
        if (processImmediately) this.processQueue();
    }

    async clearRevokedFormCache({ withinTransaction = false } = {}) {
        const clearCache = async () => {
            await db.form_templates.clear();
            await db.form_template_versions.clear();
            await db.form_assessments.clear();
            await db.form_assessment_revisions.clear();
            await db.form_assessment_drafts.clear();

            const formEntityTypes = new Set([
                'form_template', 'form_template_version', 'form_assessment',
                'form_assessment_revision', 'form_assessment_submit',
                'form_assessment_correction', 'form_assessment_entered_in_error',
                'clinic_template_offer'
            ]);
            const allQueueItems = await db.sync_queue.toArray();
            const formQueueIds = allQueueItems
                .filter(item => formEntityTypes.has(item.entity) || (item.entity && item.entity.startsWith('form_')))
                .map(item => item.id);
            if (formQueueIds.length) {
                await db.sync_queue.bulkDelete(formQueueIds);
            }
        };

        if (withinTransaction) {
            await clearCache();
            return;
        }

        await db.transaction('rw', db.form_templates, db.form_template_versions, db.form_assessments, db.form_assessment_revisions, db.form_assessment_drafts, db.sync_queue, clearCache);
    }

    async clearData() {

        addSyncBreadcrumb('clear_data_started');
        this.token = null;
        this.user_id = null;
        this.isSyncing = false;
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }

        await db.transaction('rw', db.users, db.patients, db.appointments, db.facility_charges, db.appointment_notes, db.settings, db.sync_queue, db.clinics, db.clinic_members, db.patient_doctor_links, db.fee_agreements, db.invoices, db.plans, db.user_subscriptions, db.user_addons, db.addons, db.media_cache, db.form_templates, db.form_template_versions, db.form_assessments, db.form_assessment_revisions, db.form_assessment_drafts, async () => {
            await db.users.clear();
            await db.patients.clear();
            await db.appointments.clear();
            await db.facility_charges.clear();
            await db.appointment_notes.clear();
            await db.settings.clear();
            await db.sync_queue.clear();
            await db.clinics.clear();
            await db.clinic_members.clear();
            await db.patient_doctor_links.clear();
            await db.fee_agreements.clear();
            await db.invoices.clear();
            await db.plans.clear();
            await db.user_subscriptions.clear();
            await db.user_addons.clear();
            await db.addons.clear();
            await db.media_cache.clear();  // Clear cached media on logout
            await db.form_templates.clear();
            await db.form_template_versions.clear();
            await db.form_assessments.clear();
            await db.form_assessment_revisions.clear();
            await db.form_assessment_drafts.clear();
        });


        localStorage.removeItem('last_sync');
        this.isInitialSyncComplete = false;
        this.notifySyncStateChange();
        addSyncBreadcrumb('clear_data_complete');
    }
}

export const syncService = new SyncService();
export default syncService;

if (typeof window !== 'undefined' && window.__TEST__) {
    window.syncService = syncService;
}
